import { defineStore } from 'pinia'
import { PRICES_STORE_ID } from '../constants'
import type { schemaType as cottageDetailSchemaType } from '~~/modules/api/rurality/v3/cottages/detail/schema'
import type { schemaType as cottageConditionsSchemaType } from '~~/modules/api/rurality/v3/cottages/conditions/schema'
import type { schemaType as rentUnitsDetailSchemaType } from '~~/modules/api/rurality/v3/cottages/rent-units-detail/schema'
import type { schemaType as rentUnitsPricesSchemaType } from '~~/modules/api/rurality/v3/cottages/rent-units-price/schema'
import type { schemaType as rentUnitsStructureSchemaType } from '~~/modules/api/rurality/v3/cottages/rent-units-structure/schema'
import type { schemaType as cottageOwnerDetailSchemaType } from '~~/modules/api/rurality/v3/owners/detail/schema'

export interface pricesStoreRentUnitExtraInterface {
  id: number
  type: string
  units: number | null
  people: number | null
  days: number | null
  unitPrice: number
  totalPrice: number
}

export interface selectedRentUnits {
  id: string
  selectedPeople: number
  selectedExtras: pricesStoreRentUnitExtraInterface[]
}

export interface pricesStoreInterface {
  cottageId: string
  numPeople: number
  dateIn: string
  dateOut: string
  nights: number
  total: number
  advance: number
  conditionsId: string
  cottageDetail: cottageDetailSchemaType | null
  cottageOwnerDetail: cottageOwnerDetailSchemaType | null
  rentUnitsDetail?: rentUnitsDetailSchemaType | null
  rentUnitsStructure?: rentUnitsStructureSchemaType | null
  cottageConditions: cottageConditionsSchemaType | null
  rentUnitsPrices: rentUnitsPricesSchemaType | null
  selectedRentUnits: selectedRentUnits[]
}

export const usePricesStore = defineStore(PRICES_STORE_ID, () => {
  // State
  const cottageId = ref('')
  const numPeople = ref(0)
  const dateIn = ref('')
  const dateOut = ref('')
  const nights = ref(0)
  const total = ref(0)
  const advance = ref(0)
  const conditionsId = ref('')
  const cottageDetail = ref<cottageDetailSchemaType | null>(null)
  const cottageOwnerDetail = ref<cottageOwnerDetailSchemaType | null>(null)
  const rentUnitsDetail = ref<rentUnitsDetailSchemaType | null>(null)
  const rentUnitsStructure = ref<rentUnitsStructureSchemaType | null>(null)
  const rentUnitsPrices = ref<rentUnitsPricesSchemaType | null>(null)
  const cottageConditions = ref<cottageConditionsSchemaType | null>(null)
  const selectedRentUnits = ref<selectedRentUnits[]>([])

  // Actions
  const isInitialState = (): boolean => {
    return total.value === 0 && selectedRentUnits.value.length === 0
  }

  const setInitialData = ({
    dateIn: initialDateIn,
    dateOut: initialDateOut,
    numPeople: initialNumPeople,
  }: {
    dateIn: string
    dateOut: string
    numPeople: number
  }) => {
    dateIn.value = initialDateIn
    dateOut.value = initialDateOut
    nights.value = calculateNights(initialDateIn, initialDateOut)
    numPeople.value = initialNumPeople
  }

  function setPricesData() {
    cottageId.value = cottageDetail.value?.id || ''
    total.value = getTotalPrice.value
    advance.value = getAdvanceAmount.value
    conditionsId.value =
      cottageConditions.value?.cottageConditions?.conditionsId || ''
  }

  function setCottageDetail(newCottageDetail: cottageDetailSchemaType) {
    cottageDetail.value = newCottageDetail
  }

  function setCottageOwnerDetail(
    newCottageOwnerDetail: cottageOwnerDetailSchemaType,
  ) {
    cottageOwnerDetail.value = newCottageOwnerDetail
  }

  function setRentUnitsDetail(newRentUnitsDetail: rentUnitsDetailSchemaType) {
    rentUnitsDetail.value = newRentUnitsDetail
  }

  function setRentUnitsStructure(
    newRentUnitsStructure: rentUnitsStructureSchemaType,
  ) {
    rentUnitsStructure.value = newRentUnitsStructure
  }

  function setCottageConditions(
    newCottageConditions: cottageConditionsSchemaType,
  ) {
    cottageConditions.value = newCottageConditions
  }

  function setRentUnitsPrices(newRentUnitsPrices: rentUnitsPricesSchemaType) {
    rentUnitsPrices.value = newRentUnitsPrices
  }

  function toggleRentUnit(rentUnitId: string, selectedPeople: number = 1) {
    const existingIndex = selectedRentUnits.value.findIndex(
      ru => ru.id === rentUnitId,
    )

    if (existingIndex !== -1) {
      selectedRentUnits.value.splice(existingIndex, 1)
    } else {
      selectedRentUnits.value.push({
        id: rentUnitId,
        selectedPeople,
        selectedExtras: [],
      })
    }
  }

  function updateRentUnitPeople(rentUnitId: string, people: number) {
    const existing = selectedRentUnits.value.find(ru => ru.id === rentUnitId)
    if (existing) {
      existing.selectedPeople = people
    }
  }

  function isRentUnitSelected(rentUnitId: string): boolean {
    return selectedRentUnits.value.some(ru => ru.id === rentUnitId)
  }

  function getRentUnitSelectedPeople(rentUnitId: string): number | null {
    const existing = selectedRentUnits.value.find(ru => ru.id === rentUnitId)
    return existing?.selectedPeople || null
  }

  function setRentUnitExtras(
    rentUnitId: string,
    extras: pricesStoreRentUnitExtraInterface[],
  ) {
    const existing = selectedRentUnits.value.find(ru => ru.id === rentUnitId)
    if (existing) {
      existing.selectedExtras = extras || []
    }
  }

  function clearRentUnitSelections() {
    selectedRentUnits.value = []
  }

  // Computed getters
  const getCottageDetail = computed(() => cottageDetail.value)
  const getSelectedRentUnits = computed(() => {
    return getRentUnits.value.filter(rentUnit => {
      return selectedRentUnits.value.some(
        selected => selected.id === rentUnit.id,
      )
    })
  })

  const getRentUnits = computed(() => {
    if (
      rentUnitsPrices.value?.rentUnitsPrice &&
      rentUnitsDetail.value?.rentUnits
    ) {
      return rentUnitsPrices.value.rentUnitsPrice
        .map(priceItem =>
          rentUnitsDetail.value?.rentUnits.find(
            unit => unit.id === priceItem.rentUnitId,
          ),
        )
        .filter((unit): unit is NonNullable<typeof unit> => unit !== undefined)
    }

    return rentUnitsDetail.value?.rentUnits || []
  })

  const getRentUnitById = (rentUnitId: string) => {
    return getRentUnits.value.find(rentUnit => rentUnit.id === rentUnitId)
  }

  const getRentUnitPricesById = (rentUnitId: string) => {
    return rentUnitsPrices.value?.rentUnitsPrice?.find(
      rup => rup.rentUnitId === rentUnitId,
    )
  }

  const canAutoSelect = (rentUnitId: string): boolean => {
    const rentUnit = getRentUnitPricesById(rentUnitId)
    if (!rentUnit) return false
    return !rentUnit.hasNoDatesAvailable && !rentUnit.isMinNightsRequired
  }

  const getCottageType = () => {
    return rentUnitsStructure.value?.rentUnitStructure || undefined
  }

  const getRentUnitSelectionById = (rentUnitId: string) => {
    return selectedRentUnits.value.find(ru => ru.id === rentUnitId)
  }

  const getOnlineBookingData = computed(() => {
    const route = useRoute()
    const selectedRentUnitsDetail = getSelectedRentUnits.value

    const rentUnitsForAPI = selectedRentUnitsDetail.map(rentUnit => {
      const renUnitePrices = getRentUnitPricesById(rentUnit.id)
      const rentUnitSelection = getRentUnitSelectionById(rentUnit.id)
      const priceData = renUnitePrices?.pricesByPlaces?.find(
        p => p.places === rentUnitSelection?.selectedPeople,
      )
      return {
        id: rentUnit.id,
        people: rentUnitSelection?.selectedPeople || 1,
        additional: null as number | null,
        price: priceData?.price?.amount || 0,
        name: rentUnit.name,
        extras:
          rentUnitSelection?.selectedExtras?.map(extra => ({
            id: extra.id,
            people: extra.people,
            days: extra.days,
            units: extra.units,
            unitPrice: extra.unitPrice,
            totalPrice: extra.totalPrice,
            type: extra.type,
          })) ?? [],
      }
    })

    return {
      cottageId: cottageDetail.value?.id || '',
      numPeople: getTotalSelectedPeople.value,
      dateIn: route.query.dateIn as string,
      dateOut: route.query.dateOut as string,
      nights: nights.value,
      total: getTotalPrice.value,
      advance: getAdvanceAmount.value,
      conditionsId:
        cottageConditions.value?.cottageConditions?.conditionsId || '',
      rentUnits: rentUnitsForAPI,
    }
  })

  const getPrebookingData = computed(() => {
    return {
      cottageId: cottageId.value,
      numPeople: numPeople.value,
      dateIn: dateIn.value,
      dateOut: dateOut.value,
      nights: nights.value,
      total: total.value,
      advance: advance.value,
      conditionsId: conditionsId.value,
      rentUnits: selectedRentUnits,
    }
  })

  const getPrebookingQueryParams = computed(() => {
    const parsedRentUnits = getSelectedRentUnits.value.map(rentUnit => {
      const rentUnitSelection = getRentUnitSelectionById(rentUnit.id)
      const rentUnitPrices = getRentUnitPricesById(rentUnit.id)
      const priceData = rentUnitPrices?.pricesByPlaces?.find(
        p => p.places === rentUnitSelection?.selectedPeople,
      )

      return {
        id: rentUnit.id,
        people: rentUnitSelection?.selectedPeople || 1,
        price: priceData?.price.amount || 0,
        additional: null as number | null,
        extras:
          rentUnitSelection?.selectedExtras?.map(extra => ({
            id: extra.id,
            price: extra.totalPrice,
            people: extra.people,
            units: extra.units,
            days: extra.days,
          })) ?? [],
      }
    })

    return {
      source: 'ESCAPADA_RURAL' as const,
      channel: 'EMAIL' as const,
      cottageId: cottageId.value,
      ownerId: cottageOwnerDetail.value?.id || '',
      rentUnits: parsedRentUnits,
      dateIn: dateIn.value,
      dateOut: dateOut.value,
      numPeople: getTotalSelectedPeople.value,
      totalAmount: getTotalPrice.value,
    }
  })

  const getAdvancePercentage = computed(() => {
    return (
      cottageConditions.value?.cottageConditions?.cottageConditionsAdvance
        ?.percent || null
    )
  })

  const getAdvanceAmount = computed(() => {
    const percentage =
      cottageConditions.value?.cottageConditions?.cottageConditionsAdvance
        ?.percent
    if (percentage) {
      const totalPrice = getTotalPrice.value
      return Math.round((totalPrice * percentage) / 100)
    }
    return 0
  })

  const hasSelectedRentUnits = computed(
    () => selectedRentUnits.value.length > 0,
  )
  const getTotalSelectedPeople = computed(() => {
    return selectedRentUnits.value.reduce((total, rentUnit) => {
      return total + rentUnit.selectedPeople
    }, 0)
  })

  const calculateTotalPeopleFromRentUnits = (): number => {
    return selectedRentUnits.value.reduce((total, rentUnit) => {
      return total + rentUnit.selectedPeople
    }, 0)
  }

  const hasExtrasAvailable = computed(() => {
    const rentUnits = rentUnitsDetail.value?.rentUnits || []
    return rentUnits.some(
      rentUnit => rentUnit.extras && rentUnit.extras.length > 0,
    )
  })

  const getTotalPrice = computed(() => {
    let total = 0

    selectedRentUnits.value.forEach(selectedRentUnit => {
      const priceData = rentUnitsPrices.value?.rentUnitsPrice?.find(
        rup => rup.rentUnitId === selectedRentUnit.id,
      )
      const priceByPlaces = priceData?.pricesByPlaces?.find(
        p => p.places === selectedRentUnit.selectedPeople,
      )

      if (priceByPlaces) {
        total += priceByPlaces.price.amount
      }

      selectedRentUnit.selectedExtras.forEach(extra => {
        total += extra.totalPrice
      })
    })
    return total
  })

  const hasSelectedRentUnitsWithExtrasAvailable = computed(() => {
    return getRentUnits.value.some(rentUnit => {
      const isSelected = selectedRentUnits.value.some(
        selected => selected.id === rentUnit.id,
      )
      return isSelected && rentUnit.extras && rentUnit.extras.length > 0
    })
  })

  const getSelectedRentUnitsWithExtrasAvailable = computed(() => {
    return getRentUnits.value.filter(rentUnit => {
      const isSelected = selectedRentUnits.value.some(
        selected => selected.id === rentUnit.id,
      )
      return isSelected && rentUnit.extras && rentUnit.extras.length > 0
    })
  })

  const autoSelectSingleUnit = () => {
    if (rentUnitsStructure.value?.rentUnitStructure === 'SINGLE_UNIT') {
      const singleRentUnit = getRentUnits.value[0]

      if (canAutoSelect(singleRentUnit.id)) {
        toggleRentUnit(singleRentUnit.id, numPeople.value)
      }
    }
  }

  const isFreeCancellation = computed(() => {
    return cottageConditions.value?.isFreeCancellation || false
  })

  const getFreeCancellationDays = computed(() => {
    return (
      cottageConditions.value?.cottageConditions?.cancellationPolicy.values.filter(
        policy => policy.percent === 100,
      )[0]?.days || null
    )
  })

  return {
    // State
    cottageId: cottageId,
    numPeople: numPeople,
    dateIn: dateIn,
    dateOut: dateOut,
    nights: nights,
    total: total,
    advance: advance,
    conditionsId: conditionsId,
    cottageDetail: cottageDetail,
    cottageOwnerDetail: cottageOwnerDetail,
    rentUnitsDetail: rentUnitsDetail,
    rentUnitsStructure: rentUnitsStructure,
    rentUnitsPrices: rentUnitsPrices,
    cottageConditions: cottageConditions,
    selectedRentUnits: selectedRentUnits,

    // Getters
    getCottageDetail,
    getSelectedRentUnits,
    getRentUnits,
    getOnlineBookingData,
    getPrebookingData,
    getPrebookingQueryParams,
    getAdvancePercentage,
    getAdvanceAmount,
    hasSelectedRentUnits,
    getTotalSelectedPeople,
    hasExtrasAvailable,
    getTotalPrice,
    hasSelectedRentUnitsWithExtrasAvailable,
    getSelectedRentUnitsWithExtrasAvailable,
    isFreeCancellation,
    getFreeCancellationDays,

    // Actions
    isInitialState,
    setInitialData,
    setPricesData,
    setCottageDetail,
    setCottageOwnerDetail,
    setRentUnitsDetail,
    setRentUnitsStructure,
    setCottageConditions,
    setRentUnitsPrices,
    toggleRentUnit,
    updateRentUnitPeople,
    isRentUnitSelected,
    getRentUnitSelectedPeople,
    getRentUnitById,
    getRentUnitPricesById,
    getRentUnitSelectionById,
    setRentUnitExtras,
    clearRentUnitSelections,
    canAutoSelect,
    calculateTotalPeopleFromRentUnits,
    getCottageType,
    autoSelectSingleUnit,
  }
})

type PricesStore = ReturnType<typeof usePricesStore>
export type PrebookingData = PricesStore['getPrebookingData']
export type ShowRentUnits = PricesStore['getRentUnits']
export type CottageDetail = PricesStore['getCottageDetail']
